import { useState, useEffect } from "react";
import { Reveal } from "./Reveal";
import { RichText } from "./RichText";
import { mediaUrl, sizedUrl, srcSetFor } from "../lib/api";

const hasText = (html) => !!(html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

// Text + optional uncropped image.
// Desktop: a portrait image sits to the left of the text, a landscape one below it.
// Mobile: the image always sits below the text.
// The orientation comes from the stored dimensions so the layout never shifts after load.
export const TextWithImage = ({ imageUrl, width, height, caption, testid, children }) => {
  const known = width > 0 && height > 0;
  const [measured, setMeasured] = useState(null);

  useEffect(() => {
    if (!imageUrl || known) return;
    const img = new window.Image();
    img.onload = () => setMeasured({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = mediaUrl(imageUrl);
  }, [imageUrl, known]);

  if (!imageUrl) {
    return <div className="md:max-w-[70%]">{children}</div>;
  }

  const w = known ? width : measured?.w;
  const h = known ? height : measured?.h;
  const portrait = w && h ? h > w : false;

  const picture = (
    <Reveal className="text-left">
      <img src={sizedUrl(imageUrl, 1200)} srcSet={srcSetFor(imageUrl, [480, 800, 1200])} sizes="(max-width: 767px) 100vw, 40vw" alt="" data-testid={testid}
        style={w && h ? { aspectRatio: `${w} / ${h}` } : undefined}
        className={`w-full h-auto object-contain object-left ${portrait ? "max-h-[80vh]" : ""}`} />
      {hasText(caption) && (
        <RichText html={caption} testid={`${testid}-caption`}
          className="contacts-rich font-mono text-[11px] leading-relaxed tracking-[0.08em] text-[var(--vsl-muted)] mt-3 text-left" />
      )}
    </Reveal>
  );

  if (portrait) {
    return (
      <div className="flex flex-col gap-10 md:flex-row md:items-start md:gap-8">
        <div className="order-2 md:order-1 md:w-[38%] md:shrink-0">{picture}</div>
        <div className="order-1 md:order-2 md:flex-1">{children}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-14">
      <div className="md:max-w-[70%]">{children}</div>
      {picture}
    </div>
  );
};
